import { FormError } from 'baseApi';

export enum MfaChallengeType {
  SMS = 'SMS',
  Totp = 'Totp',
  Email = 'Email',
}

// The profile endpoint sends these as the API enum's numbers; this order must match it.
const MFA_TYPES_BY_NUMBER = [
  MfaChallengeType.SMS,
  MfaChallengeType.Totp,
  MfaChallengeType.Email,
];

const SUPPORTED_MFA_TYPES = [MfaChallengeType.Totp, MfaChallengeType.Email];

export const parseMfaChallengeTypes = (
  values?: unknown[] | null
): MfaChallengeType[] =>
  (values ?? [])
    .map((value) =>
      typeof value === 'number'
        ? MFA_TYPES_BY_NUMBER[value]
        : MFA_TYPES_BY_NUMBER.find((type) => type === value)
    )
    .filter((type): type is MfaChallengeType => !!type);

export const supportedMfaChallengeTypes = (values?: unknown[] | null) => {
  const parsed = parseMfaChallengeTypes(values);
  return SUPPORTED_MFA_TYPES.filter((type) => parsed.includes(type));
};

export const MFA_REQUIRED_CODE = 'Mfa_Required';

export type MfaChallengeResult = { mfaToken: string };

export type TotpQrCodeResult = {
  qrCode: string;
  sharedSecret: string;
  mfaToken: string;
};

export type MfaGrantResult = {
  deviceTrusted: boolean;
  trustedExpiryDays: number;
  sessionGrantExpiryHours: number;
  sessionGrantMaxHours?: number;
};

export type MfaCodeSubmission = {
  challengeType: MfaChallengeType;
  mfaToken: string;
  mfaCode: string;
  trustThisDevice: boolean;
};

export type MfaRequestStatus = {
  bMfaEnabled: boolean;
  isMainAccountHolder?: boolean;
};

export type IpWhitelistEntry = {
  schoolAccountsIpAddressWhitelistId: number;
  ipAddress: string;
  addDate: string;
};

const MFA_TYPE_LABELS: Record<MfaChallengeType, string> = {
  [MfaChallengeType.SMS]: 'Text message',
  [MfaChallengeType.Totp]: 'Authentication app',
  [MfaChallengeType.Email]: 'Email address',
};

export const mfaTypeLabel = (type: MfaChallengeType) =>
  MFA_TYPE_LABELS[type] ?? type;

export type MfaLayout = 'page' | 'modal';

export const mfaCodeInstruction = (
  type: MfaChallengeType,
  layout: MfaLayout
) => {
  switch (type) {
    case MfaChallengeType.Totp:
      return layout === 'page'
        ? 'Enter the code from your authentication app.'
        : 'Enter the code displayed on your authenticator app.';
    case MfaChallengeType.SMS:
      return 'Enter the code sent to your phone.';
    default:
      return 'Enter the code sent to your email address.';
  }
};

// The session grant is an idle timeout: only the MFA-protected tools keep it going, up to sessionGrantMaxHours.
export const MFA_SET_UP_MESSAGE = 'Your MFA has now been successfully set up.';

export const describeMfaGrant = (result: MfaGrantResult) =>
  result.deviceTrusted
    ? `You won't need to verify again on this device for ${result.trustedExpiryDays} days.`
    : result.sessionGrantMaxHours
    ? `You won't need to verify again for ${result.sessionGrantExpiryHours} hours. If you continue using pupil tools, this will be extended for up to ${result.sessionGrantMaxHours} hours.`
    : `You won't need to verify again for ${result.sessionGrantExpiryHours} hours.`;

const UNKNOWN_ERROR_MESSAGE = 'Sorry, something went wrong. Please try again.';

const MFA_ERROR_MESSAGES: Record<string, string> = {
  Mfa_Code_Invalid: 'That code is not right. Please check it and try again.',
  Mfa_Token_Expired: 'That code has expired. Please request a new one.',
  Mfa_Token_Invalid: 'That code is no longer valid. Please request a new one.',
  Mfa_Challenge_Type_Invalid:
    'That verification method is not available for your account.',
  Email_Address_Missing: 'Your account has no email address to send a code to.',
  Mobile_Phone_Number_Missing:
    'Your account has no phone number to send a code to.',
  Mfa_Mandatory:
    'You need another verification method enabled before you can disable this one.',
  Mfa_Required: 'Please verify your identity first.',
  MAH_Required: 'Only the main account holder can change this setting.',
  Mfa_Already_Set: 'MFA is already set that way. Please refresh the page.',
  Invalid_IpAddress_Length: 'Please enter a valid IP address.',
  Required: 'Please complete all required fields.',
};

const firstMessageCode = (errors?: FormError[] | null) =>
  errors && errors.length > 0 ? errors[0].messageCode : '';

export const hasMessageCode = (
  errors: FormError[] | null | undefined,
  code: string
) => !!errors && errors.some((e) => e.messageCode === code);

export const parseMfaError = (errors?: FormError[] | null) =>
  MFA_ERROR_MESSAGES[firstMessageCode(errors)] ?? UNKNOWN_ERROR_MESSAGE;
